import { z } from "zod";

/**
 * Abonelik, lisans ve hak çekirdeği (CLOS-196 `[032]`).
 *
 * Bu dosya **sözleşmeyi** taşıyor: lisans durumları, izin verilen geçişler ve hak
 * anahtarları. Paket TANIMLARI burada değil — plan adları, ticari metinler ve sayısal
 * limitler konfigürasyondan gelir (kartın açık kararı: *"bu değerler kod içine
 * gömülmez"*).
 *
 * Ayrım kritik: **durum makinesi ürün kuralıdır ve değişmez**, **plan limitleri ticari
 * karardır ve değişir**. İkisini aynı yere koymak, bir fiyat değişikliğinin lisans
 * mantığını gözden geçirmeyi gerektirmesi demekti.
 */

/**
 * Lisans durumları (kart: *"deneme, aktif, ödeme bekleniyor, ek süre, dönem sonunda
 * iptal, Kısıtlı ve sona erdi"*).
 */
export const LICENSE_STATUSES = [
  "trialing",
  "active",
  "past_due",
  "grace",
  "cancel_at_period_end",
  "restricted",
  "expired",
] as const;
export type LicenseStatus = (typeof LICENSE_STATUSES)[number];

export const LICENSE_STATUS_NAMES: Record<LicenseStatus, string> = {
  trialing: "Deneme",
  active: "Aktif",
  past_due: "Ödeme bekleniyor",
  grace: "Ek süre",
  cancel_at_period_end: "Dönem sonunda iptal",
  restricted: "Kısıtlı",
  expired: "Sona erdi",
};

/**
 * İzin verilen geçişler — AÇIK bir durum makinesi (kart kriteri).
 *
 * Açıkça yazılmasaydı her yeni akış kendi geçişini uydurur ve *"bir abonelik nasıl
 * Kısıtlı'ya düşer?"* sorusunun cevabı kod tabanını taramaktan geçerdi.
 *
 * Tasarım notları:
 * - `expired` TERMİNAL: sona ermiş bir abonelik canlandırılmaz, yenisi açılır. Geri
 *   dönüş serbest olsaydı geçmiş dönemler ile yeni dönem aynı satırda karışırdı.
 * - `restricted` → `active` var: ödeme geldiğinde kısıt kalkar. Kısıt bir CEZA değil,
 *   bir DURUM.
 * - `cancel_at_period_end` → `active` var: kullanıcı iptalinden vazgeçebilmeli.
 */
export const LICENSE_TRANSITIONS: Record<LicenseStatus, readonly LicenseStatus[]> = {
  trialing: ["active", "expired", "restricted", "cancel_at_period_end"],
  active: ["past_due", "cancel_at_period_end", "restricted", "expired"],
  past_due: ["grace", "active", "restricted", "expired"],
  grace: ["active", "restricted", "expired"],
  cancel_at_period_end: ["active", "expired"],
  restricted: ["active", "expired"],
  expired: [],
};

export function canTransition(from: LicenseStatus, to: LicenseStatus): boolean {
  return LICENSE_TRANSITIONS[from].includes(to);
}

/**
 * Ürünü KULLANILABİLİR kılan durumlar.
 *
 * `restricted` ve `expired` dışarıda: kısıtlı bir abonelikte yalnız kapasite ARTIRAN
 * komutlar durur (CLOS-199), okuma durmaz — ama hak değerlendirmesi bunu tek başına
 * karara bağlamaz. Bu liste "hak veriyor mu?" sorusunun cevabıdır.
 */
export function isEntitlingStatus(status: LicenseStatus): boolean {
  return status !== "restricted" && status !== "expired";
}

/**
 * Hak anahtarları — sayısal ya da mantıksal sınırlar.
 *
 * Değerleri BURADA YOK: her paketin kaç koltuk verdiği konfigürasyondan gelir. Burada
 * yalnız *"hangi haklar var"* sorusunun kanonik cevabı duruyor ki tüketiciler yazım
 * hatasıyla var olmayan bir hakkı sormasın.
 */
export const ENTITLEMENT_KEYS = [
  /** İç kullanıcı koltuğu — CLOS-199 kapasite motorunun tükettiği hak. */
  "internal_seats",
  /**
   * AYNI ANDA yönetilen aktif sözleşme kapasitesi.
   *
   * ⚠ Bu hak DURUM bazlı: arşivlenmemiş her kayıt tüketiyor, arşivleme serbest bırakıyor.
   * Dönemle ilgisi yok — bir sözleşme yıllarca aktif kalabilir ve kapasiteyi tutmaya
   * devam eder.
   */
  "contracts",
  /**
   * DÖNEMSEL sözleşme OLUŞTURMA hakkı (CLOS-836 · Genel Ürün Kapsamı §17.4, §24.20).
   *
   * ⚠ `contracts`'tan AYRI BİR KOTA ve kanonik model ikisini ayrı sayıyor: *"Sözleşme
   * oluşturma hakkı ile aynı anda yönetilen aktif sözleşme kapasitesi ayrı kotalardır."*
   *
   * Fark ZAMAN ve DURUM ekseninde: bu hak abonelik dönemiyle YENİLENİYOR ve harcandığında
   * geri gelmiyor — arşivleme aktif kapasiteyi serbest bırakır ama harcanmış oluşturma
   * hakkını iade etmez. Tek sayaca indirilseydi "ayda 5 sözleşme" gibi bir paket, kullanıcı
   * arşivledikçe sınırsıza dönerdi.
   */
  "contract_creations",
  /** Depolama alanı (MiB). */
  "storage_mib",
  /** AI işlev kullanımı açık mı. */
  "ai_enabled",
  /**
   * DÖNEMSEL AI kredisi (KARAR 04 — CLOS-200b).
   *
   * ⚠ `ai_enabled`'dan AYRI: biri yeteneğin AÇIK olup olmadığı, diğeri o dönem KAÇ
   * kredilik iş yapılabileceği. Tek anahtara indirseydik "AI açık ama kotan bitti"
   * durumu ifade edilemez, kota bitince yetenek kapalı görünürdü.
   *
   * ⚠ Eşleme TEK YÖNLÜ: paket bu hakkı dönem açılırken `ai_credit_periods.granted`'a
   * kopyalıyor. Canlı bağlasaydık paket dönem ortasında değişince geçmiş dönemin tavanı
   * da geriye dönük değişir ve verilmiş rezervasyonlar aniden tavanı aşar hâle gelirdi.
   */
  "ai_credits",
  /**
   * Sponsorlu Müvekkil Alanı hakkı (CLOS-175 `[122]`).
   *
   * ⚠ İç kullanıcı koltuğundan AYRI bir hak: kartın bağımlılık sınırı *"sponsorlu alan
   * kapasitesi İÇ KULLANICI KOLTUĞU DEĞİLDİR"* diyor. Aynı sayaca bağlasaydık, bir
   * müvekkile alan açmak büronun kendi çalışanlarından birinin koltuğunu yerdi.
   */
  "sponsored_client_workspaces",
  /**
   * E-imza gönderimi açık mı (CLOS-167 `[160]`).
   *
   * ⚠ Kapı GÖNDERİMDE, tamamlanmada DEĞİL: kartın kuralı *"kota hatası kanonik talebi
   * sahte tamamlamaz."* Hakkı biten bir organizasyonun ATILMIŞ imzası geçersiz sayılamaz;
   * kısıtlanan şey yeni imza sürecini başlatmaktır.
   */
  "e_signature_enabled",
] as const;
export type EntitlementKey = (typeof ENTITLEMENT_KEYS)[number];

export function isKnownEntitlement(key: string): key is EntitlementKey {
  return (ENTITLEMENT_KEYS as readonly string[]).includes(key);
}

/** Hak değeri: sayısal sınır, `true`/`false` ya da SINIRSIZ. */
export type EntitlementValue = number | boolean | "unlimited";

/**
 * Bir paketin TEMEL hakları. Değerler konfigürasyondan okunur.
 */
export type PackageDefinition = {
  code: string;
  /** Hangi çalışma alanı türüne satılabilir. */
  scope: "individual" | "enterprise";
  name: string;
  /** Ticari metin — kullanıcıya gösterilir. */
  description: string;
  /** Deneme süresi (gün). `0` = deneme yok. */
  trialDays: number;
  entitlements: Partial<Record<EntitlementKey, EntitlementValue>>;
};

/**
 * Paket kataloğu konfigürasyon şeması.
 *
 * Konfigürasyon DOĞRULANIYOR: elle yazılan bir JSON'da bir yazım hatası (`internal_seat`)
 * sessizce sıfır hak demek olurdu ve arızayı ilk kullanıcı keşfederdi. Bilinmeyen anahtar
 * ve tanımsız kapsam seed anında patlıyor.
 */
export const entitlementValueSchema = z.union([
  z.number().int().nonnegative(),
  z.boolean(),
  z.literal("unlimited"),
]);

export const packageDefinitionSchema = z.object({
  code: z.string().min(1),
  scope: z.enum(["individual", "enterprise"]),
  name: z.string().min(1),
  description: z.string().min(1),
  trialDays: z.number().int().min(0),
  entitlements: z.record(z.enum(ENTITLEMENT_KEYS), entitlementValueSchema),
});

export const packageCatalogSchema = z.object({
  /**
   * Yeni çalışma alanının başlayacağı paket.
   *
   * Kurulumda abonelik açılmasaydı organizasyon hiçbir şey yapamazdı: kapasite motoru
   * hak bulamaz ve ilk davet bile gönderilemezdi. Hangi paketle başlanacağı ticari bir
   * karar, bu yüzden konfigürasyonda.
   */
  defaults: z.object({
    individual: z.string().min(1),
    enterprise: z.string().min(1),
  }),
  packages: z.array(packageDefinitionSchema).min(1),
});
export type PackageCatalog = z.infer<typeof packageCatalogSchema>;

/**
 * TEMEL hak ile EK hakkı birleştirir.
 *
 * Kart temel ve ek hakkı ayrı kayıtlar olarak istiyor; birleştirme yalnız
 * DEĞERLENDİRME anında oluyor ve hiçbir yere yazılmıyor. Yazılsaydı ek hak süresi
 * dolduğunda temel hak da bozulmuş olurdu.
 *
 * Sayısal haklar TOPLANIR, mantıksal haklar VEYA'lanır, `unlimited` her şeyi yutar.
 */
export function combineEntitlement(
  base: EntitlementValue | undefined,
  addOn: EntitlementValue | undefined,
): EntitlementValue | undefined {
  if (base === undefined && addOn === undefined) return undefined;
  if (base === "unlimited" || addOn === "unlimited") return "unlimited";
  if (typeof base === "boolean" || typeof addOn === "boolean") {
    return Boolean(base) || Boolean(addOn);
  }
  return (base ?? 0) + (addOn ?? 0);
}

/** Sayısal bir hakkın sınırı aşılıp aşılmadığı. */
export function withinLimit(value: EntitlementValue | undefined, requested: number): boolean {
  if (value === undefined) return false;
  if (value === "unlimited") return true;
  if (typeof value === "boolean") return value;
  return requested <= value;
}
